import { useGetApiV1ExamRevisionBotexamidLangGrid } from '@/shared/api/generated';
import type { ClassroomDisciplineBotExamLangGridItemViewModel } from '@/shared/api/generated/types/ClassroomDisciplineBotExamLangGridItemViewModel';
import type { LanguageOption } from '@/shared/ui/organisms/LanguageForm';
import { unwrapPagedResponse } from '@/shared/utils/unwrapPagedResponse';
import type { RevisionLang } from '../types/revision';
import { deriveAvailableLangs, toRevisionLang } from '../utils/revisionLangMapper';

/**
 * Carrega os idiomas de uma prova em revisão a partir de
 * `GET /api/v1/exam/revision/{botExamId}/lang/grid`, mapeando cada
 * `ClassroomDisciplineBotExamLangGridItemViewModel` para `RevisionLang` e
 * derivando no cliente os idiomas ainda disponíveis para adição.
 *
 * @param botExamId - ID da prova bot cujos idiomas serão listados
 * @returns Idiomas mapeados, idiomas disponíveis e estados de loading/erro
 *
 * @example
 * ```tsx
 * const { langs, availableLangs, isLoading } = useRevisionLangs(botExamId);
 * ```
 */
export function useRevisionLangs(botExamId: number) {
  const PageSize = 20;

  const query = useGetApiV1ExamRevisionBotexamidLangGrid(
    botExamId,
    { pageNumber: 1, pageSize: PageSize },
    { query: { enabled: botExamId > 0 } },
  );

  const langs: RevisionLang[] =
    unwrapPagedResponse<ClassroomDisciplineBotExamLangGridItemViewModel>(query.data as unknown).map(
      toRevisionLang,
    );

  const availableLangs: LanguageOption[] = deriveAvailableLangs(langs);

  return {
    langs,
    availableLangs,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => {
      void query.refetch();
    },
  } as const;
}
